import React from 'react';
import { addressParts, partsDroppedFor, postcodeWarning, findRegion, capitaliseWords } from './addressFormat';
import { SuburbInput } from './AddressFields';

// A country-aware Address on a form the public fills in (#395): the
// Account Application's Registered Address and the portal's delivery
// address request. The Country is picked outside this (each form places
// it its own way) and decides the parts below, their names, the required
// ones and their order (addressFormat.js).
//
// Every part is typed. An Australian Suburb offers Australia Post's Suburbs
// for the Postcode as it's typed, and notes when it isn't one of them
// (#158, SuburbInput); `fetchCheck` asks the form's own endpoint. Sending
// always goes ahead. Other countries' Postcodes are checked against their
// pattern.
//
// Each form keeps its own look: `Field` wraps one labelled part
// ({ id, label, warning, children }) and `inputStyle` styles its inputs and
// selects.

const STREET_LABELS = { line1: 'Street address', line2: 'Address line 2' };

export const EMPTY_PUBLIC_ADDRESS = { line1: '', line2: '', district: '', suburb: '', state: '', postcode: '' };

// A new Country keeps the parts both countries share and blanks the rest,
// including a State/Province that isn't on the new country's list.
export function changeCountry(address, country) {
  const next = { ...address, country, ...partsDroppedFor(country) };
  const regionPart = addressParts(country).find((p) => p.key === 'state' && p.options);
  if (regionPart && next.state && !findRegion(country, next.state)) next.state = '';
  return next;
}

function PublicAddressFields({ idPrefix, address, onChange, Field, inputStyle, fetchCheck }) {
  const { country } = address;
  const parts = addressParts(country);
  const set = (key) => (e) => onChange({ ...address, [key]: e.target.value });
  const labelOf = (part) => (part.required ? part.label : `${part.label} (optional)`);
  const postcodeNote = postcodeWarning(address);

  // The Soft Warning pattern in ui-architecture.md, styled here since the
  // site and the portal each draw their own fields.
  const suburbWarning = (message) => (
    <div style={{ fontSize: 13, color: '#7a5c00', background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 6, padding: '6px 10px', marginTop: 4, maxWidth: 560 }}>{message}</div>
  );
  const input = (part) => (part.key === 'suburb' && country === 'Australia' ? (
    <SuburbInput
      address={address}
      fetchCheck={fetchCheck}
      onChange={(fields) => onChange({ ...address, ...fields })}
      inputProps={{ id: `${idPrefix}-suburb`, required: part.required, maxLength: 100, style: inputStyle }}
      renderWarning={suburbWarning}
    />
  ) : (
    <input id={`${idPrefix}-${part.key}`} value={address[part.key] || ''} onChange={set(part.key)} required={part.required}
      onBlur={part.key === 'suburb' ? () => onChange({ ...address, suburb: capitaliseWords(address.suburb) }) : undefined}
      maxLength={part.key === 'postcode' ? 20 : 100} style={inputStyle} />
  ));
  const select = (part) => (
    <select id={`${idPrefix}-${part.key}`} value={findRegion(country, address[part.key])?.value || ''} onChange={set(part.key)} required={part.required} style={inputStyle}>
      <option value="">Choose…</option>
      {part.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  const street = parts.filter((p) => p.key === 'line1' || p.key === 'line2').map((part) => (
    <Field key={part.key} id={`${idPrefix}-${part.key}`} label={part.required ? STREET_LABELS[part.key] : `${STREET_LABELS[part.key]} (optional)`}>
      <input id={`${idPrefix}-${part.key}`} value={address[part.key] || ''} onChange={set(part.key)} required={part.required}
        onBlur={() => onChange({ ...address, [part.key]: capitaliseWords(address[part.key]) })}
        maxLength={255} autoComplete={part.key === 'line1' ? 'address-line1' : 'address-line2'} style={inputStyle} />
    </Field>
  ));
  const locality = parts.filter((p) => p.key !== 'line1' && p.key !== 'line2');

  return (
    <>
      {street}
      {locality.map((part) => (
        <Field key={part.key} id={`${idPrefix}-${part.key}`} label={labelOf(part)} warning={part.key === 'postcode' ? postcodeNote : undefined}>
          {part.options ? select(part) : input(part)}
        </Field>
      ))}
    </>
  );
}

export default PublicAddressFields;
